import { supabase } from "@/integrations/supabase/client";

export type SocialLink = { label: string; url: string };

export type PublicProfileRow = {
  user_id: string;
  is_public: boolean;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  social_links: SocialLink[];
};

export type PublicProfileLookup = {
  username: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  social_links: SocialLink[];
};

export const PUBLIC_PROFILE_LIMITS = {
  bio: 500,
  displayName: 80,
  socialLinks: 4,
  socialLinkLabel: 40,
  socialLinkUrl: 500,
  avatarBytes: 2 * 1024 * 1024,
};

export function publicProfilePath(username: string): string {
  return `/u/${encodeURIComponent(username.toLowerCase())}`;
}


export async function loadOwnPublicProfile(): Promise<PublicProfileRow | null> {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) return null;
  const { data, error } = await supabase
    .from("public_profiles")
    .select("user_id, is_public, display_name, bio, avatar_url, social_links")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    social_links: Array.isArray(data.social_links)
      ? (data.social_links as SocialLink[])
      : [],
  };
}

export async function upsertOwnPublicProfile(
  patch: Partial<Omit<PublicProfileRow, "user_id">>,
): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) throw new Error("Not signed in");
  const payload = { user_id: userId, ...patch };
  const { error } = await supabase
    .from("public_profiles")
    .upsert(payload, { onConflict: "user_id" });
  if (error) throw error;
}

export async function fetchPublicProfile(
  username: string,
): Promise<PublicProfileLookup | null> {
  const { data, error } = await supabase.rpc("get_public_profile", {
    _username: username,
  });
  if (error) throw error;
  const row = (data ?? [])[0];
  if (!row) return null;
  return {
    ...row,
    social_links: Array.isArray(row.social_links)
      ? (row.social_links as SocialLink[])
      : [],
  };
}

export async function uploadPublicAvatar(file: File): Promise<string> {
  if (file.size > PUBLIC_PROFILE_LIMITS.avatarBytes) {
    throw new Error("Avatar must be 2 MB or smaller");
  }
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) throw new Error("Not signed in");
  const ext = (file.name.split(".").pop() ?? "png").toLowerCase().slice(0, 5);
  const path = `${userId}/avatar-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from("public-avatars")
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  const { data: pub } = supabase.storage.from("public-avatars").getPublicUrl(path);
  return pub.publicUrl;
}

export function validateSocialLinks(links: SocialLink[]): string | null {
  if (links.length > PUBLIC_PROFILE_LIMITS.socialLinks) {
    return `At most ${PUBLIC_PROFILE_LIMITS.socialLinks} links`;
  }
  for (const l of links) {
    if (!l.label.trim()) return "Each link needs a label";
    if (l.label.length > PUBLIC_PROFILE_LIMITS.socialLinkLabel) {
      return `Labels must be ${PUBLIC_PROFILE_LIMITS.socialLinkLabel} chars or fewer`;
    }
    if (!/^https?:\/\//i.test(l.url)) return "Each link needs an http(s) URL";
    if (l.url.length > PUBLIC_PROFILE_LIMITS.socialLinkUrl) {
      return `URLs must be ${PUBLIC_PROFILE_LIMITS.socialLinkUrl} chars or fewer`;
    }
  }
  return null;
}
